import React, { useState } from 'react';
import { useMutation } from '@common/hooks';
import { Form, Tooltip } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  AddButton,
  Conversation,
  Divider,
  InfoTooltip,
  TextArea,
} from '@common/components';
import {
  CreateStaffingRequestApplicationCommentMutation,
  CreateStaffingRequestApplicationCommentVariables,
  CreateStaffingRequestApplicationCommentResponse,
} from 'works/graphql/create-staffing-request-application-comment';
import { Colors } from 'works/legacy/lib/style-helper';
import { CommentInfoFragmentType } from '@common/graphql';

type Props = {
  applicationId: string;
  commentable: boolean;
  canComment: { value: boolean };
  comments: CommentInfoFragmentType[];
};

export default function ApplicationProfileComments({
  applicationId,
  commentable,
  canComment,
  comments,
}: Props) {
  const [hiddenInput, setHiddenInput] = useState(true);
  const [commentsState, setCommentsState] = useState(comments);
  const [comment, setComment] = useState('');

  const [mutation, { loading }] = useMutation<
    CreateStaffingRequestApplicationCommentResponse,
    CreateStaffingRequestApplicationCommentVariables
  >(CreateStaffingRequestApplicationCommentMutation, {
    onCompleted: ({ createStaffingRequestApplicationComment }) => {
      setCommentsState((prevState) => [
        createStaffingRequestApplicationComment.comment,
        ...prevState,
      ]);
      setHiddenInput(true);
      setComment('');
    },
    onError: (error) => {
      createMessage('error', error.message);
    },
  });

  const onClick = () => {
    setHiddenInput((prevState) => !prevState);
  };

  const onFinish = (values: Store) => {
    mutation({
      variables: {
        applicationId: applicationId,
        input: {
          contentAsText: values.commentContent,
        },
      },
    });
  };

  return (
    <div className="application-profile-comments__container">
      {canComment.value && (
        <>
          {hiddenInput ? (
            <>
              {commentable ? (
                <AddButton onClick={onClick} type="default">
                  Add Note
                </AddButton>
              ) : (
                <Tooltip title="You can't add notes to this application.">
                  <AddButton disabled type="default">
                    Add Note
                  </AddButton>
                </Tooltip>
              )}
            </>
          ) : (
            <Form layout="vertical" onFinish={onFinish}>
              <Form.Item
                label={
                  <>
                    Note
                    <InfoTooltip
                      iconColor={Colors.midnightDarker}
                      leftOffset={4}
                      size="sm"
                      title="Suppliers get notified and can view notes, but will not be able to see who wrote them."
                    ></InfoTooltip>
                  </>
                }
                name="commentContent"
              >
                <TextArea
                  maxLength={1000}
                  placeholder="Add your note here."
                  onChange={(event) => setComment(event.target.value)}
                />
              </Form.Item>
              <AddButton
                htmlType="submit"
                disabled={!comment.trim()}
                loading={loading}
              >
                Add Note
              </AddButton>
            </Form>
          )}
          <Divider gutter />
        </>
      )}
      <Conversation
        posts={commentsState.map((comment) => ({
          id: comment.id,
          message: comment.contentAsText,
          name: comment.author.name,
          time: comment.updatedAt,
        }))}
      />
      <style jsx>
        {`
          .application-profile-comments__container {
            margin-top: 24px;
            max-width: 600px;
          }
        `}
      </style>
    </div>
  );
}
